// src/components/OrganizationManagement/OrganizationForm.js
import React from 'react';
import PropTypes from 'prop-types';
import ModalToggle from '../../ModalToggle';
import './OrganizationForm.css';

/**
 * OrganizationForm component for creating and editing organizations
 * Handles both create and edit modes with organization settings
 */
const OrganizationForm = ({
  mode = 'create', // 'create' or 'edit'
  formData,
  onFormDataChange,
  onSubmit,
  onCancel,
  loading = false,
  userProfile,
}) => {
  const isEditMode = mode === 'edit';
  const isFullAdmin = userProfile?.roles?.some(role => 
    (typeof role === 'string' ? role : role?.name) === 'Full Admin'
  );
  
  const handleInputChange = (field, value) => {
    onFormDataChange({ ...formData, [field]: value });
  };
  
  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };
  
  return (
    <div className="organization-form">
      <h3>{isEditMode ? 'Update Organization' : 'Create New Organization'}</h3>
      <form onSubmit={handleFormSubmit}>
        {/* Organization Name Field */}
        <div className="form-group">
          <label htmlFor="org-name">Organization Name *</label>
          <input
            id="org-name"
            type="text"
            value={formData.name || ''}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
            disabled={loading}
            placeholder="Enter organization name"
            maxLength={30}
            autoFocus
          />
        </div>
        <div className="character-counter">
          {(formData.name || '').length}/30 characters
        </div>

        {/* Organization Settings - Only show when editing */}
        {isEditMode && (
          <div className="organization-settings-section">
            <h4 className="settings-section-title">Organization Settings</h4>

            {/* Auto-Provision Credentials */}
            <div className="org-setting-item">
              <div className="org-setting-info">
                <h3>Auto-Provision Credentials</h3>
                <p>Automatically provision credentials when devices are claimed</p>
              </div>
              <div className="org-setting-control">
                <ModalToggle
                  isChecked={formData.autoProvisionCredentials || false}
                  onToggle={(newValue) =>
                    handleInputChange('autoProvisionCredentials', newValue)
                  }
                  label=""
                  disabled={loading}
                />
              </div>
            </div>

            {/* Receive Credential Notifications */}
            <div className="org-setting-item">
              <div className="org-setting-info">
                <h3>Receive Credential Notifications</h3>
                <p>Receive notifications when credentials are provisioned or updated</p>
              </div>
              <div className="org-setting-control">
                <ModalToggle
                  isChecked={formData.receiveCredentialNotifications || false}
                  onToggle={(newValue) =>
                    handleInputChange('receiveCredentialNotifications', newValue)
                  }
                  label=""
                  disabled={loading}
                />
              </div>
            </div>

            {/* Developer Streams - Only show for Full Admin */}
            {isFullAdmin && (
              <div className="org-setting-item">
                <div className="org-setting-info">
                  <h3>Enable Developer Streams for All Roles</h3>
                  <p>
                    Allow all users (including Org Admins and Org Users) to access Developer
                    Streams. By default, only Full Admins can access this feature.
                  </p>
                </div>
                <div className="org-setting-control">
                  <ModalToggle
                    isChecked={formData.enableDevStreamsForAllRoles || false}
                    onToggle={(newValue) =>
                      handleInputChange('enableDevStreamsForAllRoles', newValue)
                    }
                    label=""
                    disabled={loading}
                  />
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Buttons */}
        <div className="form-buttons">
          <button type="submit" className="btn-submit" disabled={loading}>
            {loading ? 'Processing...' : isEditMode ? 'Update' : 'Create'}
          </button>
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

OrganizationForm.propTypes = {
  mode: PropTypes.oneOf(['create', 'edit']),
  formData: PropTypes.shape({
    name: PropTypes.string,
    autoProvisionCredentials: PropTypes.bool,
    receiveCredentialNotifications: PropTypes.bool,
    enableDevStreamsForAllRoles: PropTypes.bool,
  }).isRequired,
  onFormDataChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  userProfile: PropTypes.shape({
    roles: PropTypes.arrayOf(PropTypes.string),
  }),
};

OrganizationForm.defaultProps = {
  mode: 'create',
  loading: false,
  userProfile: null,
};

export default OrganizationForm;
